import type { ReadingSlide } from "../ui/StartReader";
import { AppChip, C, Frame, MONO } from "../ui/readingArt";

/**
 * "Read while you wait" summary for the remote-work-max start screen. Three
 * short slides (what/who, the remote-work threat picture, key terms) shown in
 * the reader while the 3D room downloads. Content only; the reader UI lives in
 * ui/StartReader.tsx and the shared art toolkit in ui/readingArt.tsx.
 */

/* ------------------------------------------------------------------ */
/* 1. a working day on the road: four stops, risk under each           */
/* ------------------------------------------------------------------ */
type StopGlyph = "home" | "train" | "cafe" | "client";

function StopShape({ g }: { g: StopGlyph }) {
  const s = { fill: "none", stroke: C.mint, strokeWidth: 1.5, strokeLinecap: "round", strokeLinejoin: "round" } as const;
  switch (g) {
    case "home":
      return (
        <g {...s}>
          <polyline points="-6,0 0,-6 6,0" />
          <polyline points="-4,-1.5 -4,5 4,5 4,-1.5" />
        </g>
      );
    case "train":
      return (
        <g {...s}>
          <rect x="-5" y="-6" width="10" height="10" rx="2" />
          <line x1="-5" y1="-1" x2="5" y2="-1" />
          <line x1="-3.5" y1="6.5" x2="-5" y2="8" />
          <line x1="3.5" y1="6.5" x2="5" y2="8" />
        </g>
      );
    case "cafe":
      return (
        <g {...s}>
          <path d="M-5 -3 H4 V2 a4 4 0 0 1 -4 4 H-1 a4 4 0 0 1 -4 -4 Z" />
          <path d="M4 -1.5 h2 a2 2 0 0 1 0 4 h-2" />
        </g>
      );
    case "client":
      return (
        <g {...s}>
          <rect x="-5" y="-7" width="10" height="14" rx="1.5" />
          <line x1="-2" y1="-3" x2="-2" y2="-3" />
          <line x1="2" y1="-3" x2="2" y2="-3" />
          <line x1="-2" y1="1" x2="-2" y2="1" />
          <line x1="2" y1="1" x2="2" y2="1" />
        </g>
      );
  }
}

const STOPS: { g: StopGlyph; title: string; sub: string }[] = [
  { g: "home", title: "Home", sub: "own router" },
  { g: "train", title: "Train", sub: "screens, calls" },
  { g: "cafe", title: "Caf\u00e9", sub: "open Wi-Fi" },
  { g: "client", title: "Client", sub: "shared PC" },
];

function RoadArt() {
  const H = 296;
  return (
    <Frame
      label="Example: a working day away from the office"
      aria="A route from home to a train, a cafe and a client site, comparing the protections at the office with those on the road"
      height={H}
    >
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        {"Max\u2019s workday"}
      </text>
      <rect x="252" y="38" width="92" height="20" rx="10" fill={C.amber} fillOpacity="0.12" stroke={C.amber} strokeOpacity="0.45" />
      <text x="298" y="52" fontSize="10.5" fontWeight="700" fill={C.amber} textAnchor="middle" letterSpacing="0.5">
        ON THE ROAD
      </text>
      <line x1="16" y1="70.5" x2="344" y2="70.5" stroke={C.line} />

      <line x1="42" y1="108" x2="318" y2="108" stroke={C.line} strokeWidth="2" strokeDasharray="4 4" />
      {STOPS.map((st, i) => {
        const cx = 42 + i * 92;
        const hot = st.g === "cafe";
        return (
          <g key={st.g}>
            {hot ? <circle className="sr-anim-ring" cx={cx} cy="108" r="16" fill="none" stroke={C.amber} strokeWidth="1.5" /> : null}
            <circle cx={cx} cy="108" r="17" fill={C.panel2} stroke={hot ? C.amber : C.line} strokeOpacity={hot ? 0.8 : 1} />
            <g transform={`translate(${cx} ${108})`}>
              <StopShape g={st.g} />
            </g>
            <text x={cx} y="142" fontSize="12" fontWeight="700" fill={C.text} textAnchor="middle">
              {st.title}
            </text>
            <text x={cx} y="156" fontSize="10" fill={C.muted} textAnchor="middle">
              {st.sub}
            </text>
          </g>
        );
      })}

      <rect x="16.5" y="176.5" width="156" height="104" rx="9" fill={C.panel2} stroke={C.line} />
      <text x="30" y="197" fontSize="11" fontWeight="700" fill={C.muted} letterSpacing="0.5">
        AT THE OFFICE
      </text>
      <circle cx="33" cy="215" r="3" fill={C.mint} />
      <text x="44" y="219" fontSize="11.5" fill={C.soft}>
        Managed network
      </text>
      <circle cx="33" cy="237" r="3" fill={C.mint} />
      <text x="44" y="241" fontSize="11.5" fill={C.soft}>
        Locked doors
      </text>
      <circle cx="33" cy="259" r="3" fill={C.mint} />
      <text x="44" y="263" fontSize="11.5" fill={C.soft}>
        IT close by
      </text>

      <rect x="187.5" y="176.5" width="156" height="104" rx="9" fill={C.amber} fillOpacity="0.07" stroke={C.amber} strokeOpacity="0.45" />
      <text x="201" y="197" fontSize="11" fontWeight="700" fill={C.amber} letterSpacing="0.5">
        ON THE ROAD
      </text>
      <circle cx="204" cy="215" r="3" fill="none" stroke={C.amber} strokeWidth="1.3" />
      <text x="215" y="219" fontSize="11.5" fill={C.soft}>
        Any Wi-Fi around
      </text>
      <circle cx="204" cy="237" r="3" fill="none" stroke={C.amber} strokeWidth="1.3" />
      <text x="215" y="241" fontSize="11.5" fill={C.soft}>
        Screens in view
      </text>
      <circle cx="204" cy="259" r="3" fill="none" stroke={C.amber} strokeWidth="1.3" />
      <text x="215" y="263" fontSize="11.5" fontWeight="700" fill={C.text}>
        Mostly just you
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 2. Wi-Fi list: two near-identical open networks, VPN off            */
/* ------------------------------------------------------------------ */
function WifiBars({ x, y, color }: { x: number; y: number; color: string }) {
  return (
    <g transform={`translate(${x} ${y})`} fill={color}>
      <rect x="0" y="9" width="3.5" height="5" rx="1" />
      <rect x="6" y="5.5" width="3.5" height="8.5" rx="1" />
      <rect x="12" y="1" width="3.5" height="13" rx="1" />
    </g>
  );
}

function WifiRow({ y, name, tag, tagColor, flagged }: { y: number; name: string; tag: string; tagColor: string; flagged?: boolean }) {
  return (
    <g>
      {flagged ? <rect x="16.5" y={y - 4.5} width="327" height="36" rx="8" fill={C.amber} fillOpacity="0.08" stroke={C.amber} strokeOpacity="0.45" /> : null}
      <WifiBars x={28} y={y + 5} color={flagged ? C.amber : C.mint} />
      <text x="54" y={y + 17} fontSize="12.5" fontFamily={MONO} fill={C.text}>
        {name}
      </text>
      <text x="332" y={y + 17} fontSize="11" fontWeight="600" fill={tagColor} textAnchor="end">
        {tag}
      </text>
    </g>
  );
}

function NetworkArt() {
  const H = 300;
  return (
    <Frame
      label="Example: a station Wi-Fi list"
      aria="A Wi-Fi list with two almost identically named open networks, one of them flagged, and a note that the VPN is off"
      height={H}
    >
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        Wi-Fi networks
      </text>
      <text x="344" y="53" fontSize="11" fill={C.muted} textAnchor="end">
        Central Station
      </text>
      <line x1="16" y1="70.5" x2="344" y2="70.5" stroke={C.line} />

      <WifiRow y={80} name="StationWiFi" tag="Open" tagColor={C.muted} />
      <WifiRow y={122} name="StationWiFi-Free" tag="Open" tagColor={C.amber} flagged />
      <WifiRow y={164} name={"Max\u2019s phone"} tag="Locked" tagColor={C.mint} />

      <text x="30" y="224" fontSize="11" fill={C.muted}>
        Same look, same name. Anyone can set up the second one.
      </text>

      <rect x="16.5" y="238.5" width="327" height="46" rx="8" fill={C.red} fillOpacity="0.08" stroke={C.red} strokeOpacity="0.4" />
      <rect x="16" y="244" width="3" height="35" rx="1.5" fill={C.red} />
      <rect className="sr-anim-pulse" x="30" y="249" width="62" height="26" rx="13" fill={C.red} fillOpacity="0.14" stroke={C.red} strokeOpacity="0.5" />
      <text x="61" y="266" fontSize="11" fontWeight="700" fill={C.red} textAnchor="middle" letterSpacing="0.5">
        VPN OFF
      </text>
      <text x="104" y="259" fontSize="11.5" fontWeight="600" fill={C.text}>
        Mail and files would cross
      </text>
      <text x="104" y="273" fontSize="11.5" fontWeight="600" fill={C.text}>
        an unknown network.
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 3. pre-trip device checklist                                        */
/* ------------------------------------------------------------------ */
function ChecklistRow({ y, label, value, hot }: { y: number; label: string; value: string; hot?: boolean }) {
  return (
    <g>
      {hot ? <circle className="sr-anim-ring" cx="30" cy={y} r="8" fill="none" stroke={C.mint} strokeWidth="1.5" /> : null}
      <circle cx="30" cy={y} r="8" fill={C.mint} fillOpacity="0.16" />
      <polyline points={`26.4,${y} 29,${y + 2.8} 33.8,${y - 3}`} fill="none" stroke={C.mint} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <text x="48" y={y + 4.2} fontSize="12.5" fill={C.text}>
        {label}
      </text>
      <text x="334" y={y + 4.2} fontSize="11.5" fill={C.muted} textAnchor="end">
        {value}
      </text>
    </g>
  );
}

function ChecklistArt() {
  const H = 296;
  return (
    <Frame
      label="Example: before you leave"
      aria="A checklist of five device habits for working away from the office, and a reminder to report a lost device at once"
      height={H}
    >
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        Device checklist
      </text>
      <rect x="262" y="37" width="82" height="24" rx="12" fill={C.mint} fillOpacity="0.14" stroke={C.mint} strokeOpacity="0.45" />
      <text x="303" y="53.5" fontSize="11" fontWeight="700" fill={C.mint} textAnchor="middle" letterSpacing="0.6">
        5 OF 5
      </text>
      <line x1="16" y1="74.5" x2="344" y2="74.5" stroke={C.line} />

      <ChecklistRow y={94} label="Screen lock" value="30 sec" />
      <line x1="16" y1="108.5" x2="344" y2="108.5" stroke={C.line} strokeOpacity="0.6" />
      <ChecklistRow y={126} label="Disk encryption" value="On" />
      <line x1="16" y1="140.5" x2="344" y2="140.5" stroke={C.line} strokeOpacity="0.6" />
      <ChecklistRow y={158} label="VPN on public Wi-Fi" value="Always" hot />
      <line x1="16" y1="172.5" x2="344" y2="172.5" stroke={C.line} strokeOpacity="0.6" />
      <ChecklistRow y={190} label="Privacy screen" value="Fitted" />
      <line x1="16" y1="204.5" x2="344" y2="204.5" stroke={C.line} strokeOpacity="0.6" />
      <ChecklistRow y={222} label="Updates" value="Current" />

      <rect x="16.5" y="244.5" width="327" height="40" rx="8" fill={C.amber} fillOpacity="0.1" stroke={C.amber} strokeOpacity="0.4" />
      <circle cx="33" cy="264.5" r="5.5" fill="none" stroke={C.amber} strokeWidth="1.5" />
      <circle cx="33" cy="264.5" r="1.8" fill={C.amber} />
      <text x="46" y="268.5" fontSize="12" fontWeight="600" fill={C.amber}>
        Lost it? Report it straight away
      </text>
    </Frame>
  );
}

export const REMOTE_WORK_MAX_READING: ReadingSlide[] = [
  {
    tab: "Why it matters",
    headline: "The office walls stay behind. The risks come along.",
    illustration: RoadArt,
    caption: "Away from the office, you are the security.",
    sections: [
      {
        h: "What this is about",
        body: "A day on the road with Max, a sales manager. Home, train, caf\u00e9, client site: you decide how he protects company data at every stop.",
      },
      {
        h: "Who it\u2019s for",
        body: "Anyone who works from home, travels for work, or opens company files outside the office. No technical background needed.",
      },
      {
        h: "Why your judgement matters",
        body: "At the office, the network, the doors and IT do much of the protecting. Out here, most of it is small choices made in seconds: which network, where you sit, what you plug in. Those choices are yours.",
      },
    ],
  },
  {
    tab: "Main risks",
    headline: "Where working away from the office goes wrong.",
    illustration: NetworkArt,
    caption: "A familiar network name proves nothing.",
    sections: [
      {
        h: "Public Wi-Fi can be a trap",
        body: "Attackers copy real network names. Join the wrong one and they can watch or alter your traffic. Prefer your phone\u2019s hotspot, and switch the VPN on before you open anything.",
      },
      {
        h: "Home networks need care too",
        body: "A router with its default password or old firmware is an open door. Change the password, keep it updated, and keep work off shared or guest devices.",
      },
      {
        h: "Eyes and ears nearby",
        body: "Strangers can read your screen or overhear a call. Sit with your back to a wall, use a privacy screen, and keep sensitive talk for a private place.",
      },
      {
        h: "Devices get lost, plugged in and left behind",
        body: "A lost laptop, a public charging port or a shared PC can expose everything on it. Lock it, encrypt it, carry your own charger, sign out, and report a loss at once.",
      },
    ],
  },
  {
    tab: "Key terms",
    headline: "The words you\u2019ll meet today.",
    illustration: ChecklistArt,
    caption: "A few habits cover most of the risk.",
    sections: [
      { h: "VPN", body: "A private, encrypted tunnel from your device to your company, so a shared network can\u2019t read what passes through." },
      { h: "Evil twin", body: "A fake Wi-Fi network named like a real one, set up to capture whatever you send over it." },
      { h: "Shoulder surfing", body: "Reading a screen, or overhearing a call, over someone\u2019s shoulder." },
      { h: "Juice jacking", body: "A public charging port or cable that also tries to copy data or install software on your device." },
      { h: "Full-disk encryption", body: "Scrambles everything on a device, so a lost laptop or phone can\u2019t simply be read." },
      { h: "Deepfake call", body: "A fake voice or video of someone you trust, such as your boss, used to rush you into sending files or approving access." },
    ],
  },
];
